import { AnimatePresence, motion } from "framer-motion";
import { useState } from "react";
import { ArrowRightIcon } from "lucide-react";
import { Link } from "react-router-dom";

import { Icon } from "@/components/atoms/Icon";
import { AnimateHeight } from "@/components/molecules/AnimateHeight";
import { FieldEnum } from "@/components/organisms/Field/FieldEnum";
import { EnumColors } from "@/constants/values";
import { DropdownType } from "@/layouts/@types";
import { Button } from "@/components/ui/button";
import { Typography } from "@/components/ui/typography";
import { cn } from "@/components/ui/utils";

interface DropdownProps {
  dropdown: DropdownType[];
  dropdownKey: string | number | null;
  clearCloseTimeout: () => void;
  scheduleCloseDropdown: () => void;
  onItemSelect: () => void;
  animationDirection: "rtl" | "ltr" | "default";
}

export function Dropdown({
  dropdown,
  dropdownKey,
  clearCloseTimeout,
  scheduleCloseDropdown,
  onItemSelect,
  animationDirection,
}: DropdownProps) {
  const [hoverIdx, setHoverIdx] = useState<number | null>(null);
  const x =
    animationDirection === "default"
      ? 0
      : animationDirection === "rtl"
        ? "100px"
        : "-100px";

  return (
    <div
      className="shadow-lg bg-popover [backdrop-filter:blur(8px)] [-webkit-backdrop-filter:blur(8px)] [border-width:1px] [border-style:solid] border-border rounded-lg w-full flex overflow-hidden"
      onMouseEnter={clearCloseTimeout}
      onMouseLeave={scheduleCloseDropdown}
    >
      <AnimateHeight>
        <AnimatePresence mode="wait">
          <motion.div
            key={dropdownKey}
            initial={{ x, opacity: 0 }}
            animate={{ x: 0, opacity: 1 }}
            exit={{ x, opacity: 0 }}
            transition={{ duration: 0.1 }}
          >
            <div className="min-w-0 flex-col flex w-120 max-w-120 cursor-pointer p-4 h-fit">
              {dropdown.map((item, idx) => {
                return (
                  <Button
                    variant="ghost"
                    asChild
                    onMouseEnter={() => setHoverIdx(idx)}
                    onMouseLeave={() => setHoverIdx(null)}
                    onClick={() => onItemSelect()}
                    key={item.path}
                    className="flex flex-row p-2 justify-between items-center relative w-full"
                  >
                    <Link to={item.path}>
                      <div
                        className={cn(
                          'flex items-center justify-center relative w-10 h-10 rounded-md overflow-hidden bg-background [transition:all_0.3s] [&::before]:[content:""] [&::before]:absolute [&::before]:top-0 [&::before]:left-0 [&::before]:right-0 [&::before]:bottom-0 [&::before]:bg-[linear-gradient(var(--border)_1px,transparent_1px),linear-gradient(90deg,var(--border)_1px,transparent_1px)] [&::before]:bg-size-[12px_12px] [&::before]:bg-position-[-5px_-5px] [&::before]:z-1',
                          hoverIdx !== idx
                            ? '[&::after]:[content:""] [&::after]:absolute [&::after]:top-0 [&::after]:left-0 [&::after]:right-0 [&::after]:bottom-0 [&::after]:[box-shadow:inset_0_0_4px_4px_var(--background)] [&::after]:z-2'
                            : '[&::after]:[content:""] [&::after]:absolute [&::after]:top-0 [&::after]:left-0 [&::after]:right-0 [&::after]:bottom-0 [&::after]:[box-shadow:inset_0_0_0px_0px_transparent] [&::after]:z-2',
                          "[border-width:1px] [border-style:solid] border-[initial]",
                          hoverIdx !== idx
                            ? "border-background"
                            : "border-border",
                        )}
                      >
                        <Icon
                          ReactIcon={item.ReactIcon}
                          size="md"
                          style={{
                            color: "var(--muted-foreground)",
                            position: "relative",
                            zIndex: 3,
                          }}
                        />
                      </div>
                      <div
                        // gap: 0.5,
                        className="flex flex-col items-start justify-start text-left pl-1 pr-12"
                      >
                        <Typography
                          level="body-md"
                          className="flex items-center gap-1.5 flex-nowrap inline text-foreground"
                        >
                          {item.name}
                          <ArrowRightIcon
                            size="1em"
                            style={{
                              display: "inline",
                              transition: "all 0.3s",
                              opacity: hoverIdx === idx ? 1 : 0,
                              transform:
                                (hoverIdx === idx
                                  ? "translateX(-4px)"
                                  : "translateX(-8px)") + " translateY(3px)",
                            }}
                          />
                        </Typography>
                        <Typography
                          level="body-sm"
                          className="text-muted-foreground/80"
                        >
                          {item.tagline}
                        </Typography>
                      </div>
                      <div className="grow" />
                      {item.label && (
                        <div
                          // flexDirection: "column",
                          // border: 1,
                          className="flex absolute right-0 top-0 pt-4 pr-4 transform-[scale(0.75)] origin-[top_right]"
                        >
                          <FieldEnum
                            value={item.label}
                            valueOptions={{
                              enum: [
                                {
                                  key: "label",
                                  value: item.label,
                                },
                              ],
                              getColors(mode) {
                                return {
                                  COMING_SOON: EnumColors.PURPLE[mode],
                                };
                              },
                            }}
                            isMultiline={false}
                            style={{
                              paddingLeft: 0,
                              paddingRight: 0,
                            }}
                          />
                        </div>
                      )}
                    </Link>
                  </Button>
                );
              })}
            </div>
          </motion.div>
        </AnimatePresence>
      </AnimateHeight>
    </div>
  );
}
